/**
 * Error Classification Utility
 *
 * Classifies Axios errors into specific error types to determine
 * appropriate error handling and user messaging.
 *
 * UPDATED: Now includes structured error code support with backward compatibility
 */

import { AxiosError } from 'axios';
import { ErrorType as NewErrorType, ParsedError } from '../types/errors';
import { parseApiError, getUserMessage, isRetryable as checkRetryable } from './errorParser';

// Re-export the new ErrorType for backward compatibility
export { ErrorType } from '../types/errors';

// Legacy ErrorType alias for backward compatibility
// This allows existing code to continue working
export const LegacyErrorType = {
  SERVER_ERROR: NewErrorType.SERVER_ERROR,
  NETWORK_ERROR: NewErrorType.NETWORK_ERROR,
  AUTH_ERROR: NewErrorType.AUTH_ERROR,
  UNKNOWN: NewErrorType.UNKNOWN,
} as const;

/**
 * Classifies an Axios error into a specific error type
 *
 * @param error - The Axios error to classify
 * @returns The error type classification
 *
 * @example
 * ```typescript
 * const errorType = classifyError(axiosError);
 * if (errorType === ErrorType.NETWORK_ERROR) {
 *   showNetworkErrorMessage();
 * }
 * ```
 *
 * UPDATED: Now uses the new error parser for more detailed classification
 */
export function classifyError(error: AxiosError): NewErrorType {
  // Use the new error parser for detailed classification
  const parsed = parseApiError(error);
  return parsed.type;
}

/**
 * Get detailed error information including error code and domain
 *
 * @param error - The Axios error to parse
 * @returns Parsed error details with code, domain, and classification
 *
 * @example
 * ```typescript
 * const details = getErrorDetails(axiosError);
 * console.log(`Error ${details.code}: ${details.message}`);
 * ```
 */
export function getErrorDetails(error: AxiosError): ParsedError {
  return parseApiError(error);
}

/**
 * Get user-friendly error message from an Axios error
 *
 * @param error - The Axios error
 * @returns User-friendly error message in Portuguese
 *
 * @example
 * ```typescript
 * const message = getUserFriendlyMessage(axiosError);
 * toast.error(message);
 * ```
 */
export function getUserFriendlyMessage(error: AxiosError): string {
  return getUserMessage(error);
}

/**
 * Check if an error is retryable
 *
 * @param error - The Axios error
 * @returns true if the error can be retried, false otherwise
 *
 * @example
 * ```typescript
 * if (isErrorRetryable(axiosError)) {
 *   retryRequest();
 * }
 * ```
 */
export function isErrorRetryable(error: AxiosError): boolean {
  return checkRetryable(error);
}
